'use client'

import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect } from 'react'
import { isTelemetryAllowed, onConsentGranted } from '@/lib/consent/client'
import { announcePostHogLoaded } from '@/lib/posthog-loaded'
import { sanitizeSearchAnalyticsEvent } from '@/lib/search/analytics-privacy'

// STRICT: Only fridge.com - NO localhost, NO preview deployments, NO Vercel previews
const isProduction = typeof window !== 'undefined' &&
  window.location.hostname === 'fridge.com'

// Patterns for errors we should ignore (ad blockers, script loading, etc.)
const IGNORED_ERROR_PATTERNS = [
  /Unexpected EOF/i,
  /Failed to execute 'appendChild'/i,
  /Script error/i,
  /Loading chunk/i,
  /Loading CSS chunk/i,
  /googletagmanager/i,
  /gtag/i,
  /posthog/i,
  /analytics/i,
  /Called on script loaded before session recording is available/i,
]

function shouldIgnoreError(message: string): boolean {
  return IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(message))
}

export function PHProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Global error handler to filter out ad blocker/script loading noise
      const originalOnError = window.onerror
      window.onerror = function(message, source, lineno, colno, error) {
        const msg = String(message || '')
        const src = String(source || '')
        
        // Ignore script loading errors (ad blockers, network issues)
        if (shouldIgnoreError(msg) || shouldIgnoreError(src)) {
          return true // Prevent error from propagating
        }
        
        // Let other errors through
        if (originalOnError) {
          return originalOnError.call(this, message, source, lineno, colno, error)
        }
        return false
      }
      
      // Also catch unhandled promise rejections from script loading
      const originalUnhandled = window.onunhandledrejection
      window.onunhandledrejection = function(event) {
        const reason = String(event?.reason || '')
        if (shouldIgnoreError(reason)) {
          event.preventDefault()
          return
        }
        if (originalUnhandled) {
          return originalUnhandled.call(this, event)
        }
      }
      const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
      const host = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com'

      // STRICT: Only initialize PostHog on fridge.com production.
      // FRIDGE-115: and, for visitors the middleware marked consent-gated
      // (EEA + UK + CH), only after they choose Allow — init is what writes
      // the ph_* cookie/localStorage identifier and opens the first request
      // to the ingest host, so it must not run before consent. Outside the
      // gated regions isTelemetryAllowed() is true on the spot and this path
      // is unchanged. Before init, posthog.capture() calls are silent no-ops.
      if (key && isProduction) {
        const initPostHog = () => posthog.init(key, {
          api_host: host,
          
          // Filter out script loading errors from exception tracking
          before_send: (event) => {
            // Skip exception events for ad blocker / script loading errors
            if (event?.event === '$exception') {
              const message = String(event?.properties?.$exception_message || '')
              const type = String(event?.properties?.$exception_type || '')
              if (shouldIgnoreError(message) || shouldIgnoreError(type)) {
                return null // Drop the event
              }
            }
            if (!event) return event
            // FRIDGE-824: SDK page/referrer URLs must not retain raw search
            // input even when no explicit search event is emitted.
            // Search-page autocapture can contain query text in DOM labels;
            // useful search outcomes must use the explicit category-only event.
            return sanitizeSearchAnalyticsEvent(event, {
              hostname: window.location.hostname,
              pathname: window.location.pathname,
              userAgent: navigator.userAgent,
              automated: navigator.webdriver,
            })
          },
          
          // Pageview tracking
          capture_pageview: false, // We capture manually with metadata
          capture_pageleave: true,
          
          // SESSION RECORDING - Enabled for user journey analysis
          disable_session_recording: false,
          session_recording: {
            // Mask all text inputs for privacy
            maskAllInputs: true,
            // Block specific elements from recording
            blockSelector: '.ph-no-capture',
          },
          
          // Autocapture for clicks, form submissions, etc.
          autocapture: true,
          
          // Disable surveys (we don't use them)
          disable_surveys: true,
          
          // Persistence for user identification
          persistence: 'localStorage+cookie',
          
          // Person profiles - only for identified users
          person_profiles: 'identified_only',
          
          // Loaded callback - runs when PostHog is ready
          // PERFORMANCE: Minimal work here - defer everything heavy
          loaded: (posthog) => {
            // FRIDGE-807: captures that mounted before this effect ran (a
            // child's effect runs first) wait on this signal — the SDK drops
            // a capture sent before init. lib/posthog-loaded.ts.
            announcePostHogLoaded()

            // === BOT DETECTION (lightweight) ===
            const nav = navigator as any
            const isLikelyBot = 
              nav.webdriver === true ||
              /bot|crawl|spider|headless|prerender/i.test(navigator.userAgent)
            
            // === START SESSION RECORDING ===
            // Only for non-bots - defer to not block INP
            if (!isLikelyBot) {
              // PostHog has a race condition where calling startSessionRecording()
              // triggers a lazy load whose callback can fire before the extension
              // has registered. Swallow the resulting throw so it doesn't surface
              // in Sentry; the next user interaction retries cleanly.
              setTimeout(() => {
                try {
                  posthog.startSessionRecording()
                } catch {
                  // intentionally swallowed - see comment above
                }
              }, 3000)
            }
            
            // === DEFER ALL PROPERTY SETTING ===
            // Move to requestIdleCallback with long timeout - this is not critical path
            const setProperties = () => {
              // Only set essential properties, not device info
              const searchParams = new URLSearchParams(window.location.search)
              const utmSource = searchParams.get('utm_source')
              
              // Only set if we have UTM params (marketing attribution)
              if (utmSource) {
                posthog.capture('$set', {
                  $set_once: {
                    first_utm_source: utmSource,
                    first_utm_medium: searchParams.get('utm_medium'),
                    first_utm_campaign: searchParams.get('utm_campaign'),
                    first_landing_page: window.location.pathname,
                  }
                })
              }
            }
            
            // Defer property setting with very long timeout - not critical
            if ('requestIdleCallback' in window) {
              (window as any).requestIdleCallback(setProperties, { timeout: 5000 })
            } else {
              setTimeout(setProperties, 3000)
            }
          }
        })
        if (isTelemetryAllowed()) {
          initPostHog()
        } else {
          // Gated and undecided: wait for the banner's Allow. Unsubscribed on
          // unmount so a strict-mode double effect never inits twice.
          return onConsentGranted(initPostHog)
        }
      }
    }
  }, [])

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}
